/**
 * Pure view model of the «Онлайн-квота партнёра» block (Settings, contract #29 —
 * ADDITIVE v1.8.0, wave 9, T.Y9, D2/A14).
 *
 * Framework-free: type-only imports, no api client, no React. The numbers, the
 * RU sentences and the one warning the owner asked for live here.
 */

/** `''` and non-digits are the empty draft, not a zero — a pool is never implied. */
export function parsePoolInput(value: string): { value: number | null; error: string | undefined } {
  const trimmed = value.trim();
  if (trimmed === '') return { value: null, error: undefined };
  if (!/^\d+$/.test(trimmed)) return { value: null, error: 'Квота — целое число мест.' };
  const parsed = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(parsed) || parsed < 0) return { value: null, error: 'Квота — целое число мест.' };
  return { value: parsed, error: undefined };
}

/**
 * The warning the block prints when the new pool is BELOW the rows the partner
 * already carries: lowering is legal on purpose (SR §7.4/R13) — the live rows
 * stay, are matched and edited as before, and only NEW prefill rows are refused
 * until the pool catches up. `used` travels only on #29's own answer, so a block
 * that has not saved once yet cannot know it and warns on nothing.
 */
export const POOL_LOW_WARNING = 'Строки останутся, новые добавить нельзя.';

export function poolLowWarning(
  nextPlan: number | null | undefined,
  used: number | null | undefined,
): string | null {
  if (typeof nextPlan !== 'number' || !Number.isFinite(nextPlan)) return null;
  if (typeof used !== 'number' || !Number.isFinite(used)) return null;
  return nextPlan < used ? POOL_LOW_WARNING : null;
}

/** «использовано N из M» — the sentence SR §5.3 pins for this block. `used` may be unknown. */
export function poolUsageLabel(plan: number | null | undefined, used: number | null | undefined): string {
  const planPart = typeof plan === 'number' && Number.isFinite(plan) ? plan.toLocaleString('ru-RU') : '—';
  const usedPart = typeof used === 'number' && Number.isFinite(used) ? used.toLocaleString('ru-RU') : null;
  return usedPart === null ? `использовано: — из ${planPart}` : `использовано ${usedPart} из ${planPart}`;
}

/**
 * A save is worth a PATCH only when the number moved: the api refuses an empty
 * body and an unchanged one is noise in the audit trail it writes.
 */
export function poolChanged(current: number | null | undefined, next: number | null): boolean {
  if (next === null) return false;
  return current !== next;
}
